import { useMemo } from "react";
import {
  Authenticated,
  CanAccess,
  useList,
  useTranslate,
} from "@refinedev/core";
import {
  Card,
  Result,
  Space,
  Table,
  TableColumnsType,
  Tag,
  Typography,
} from "antd";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { DateField, EditButton, DeleteButton, List } from "@refinedev/antd";
import { addDateTz } from "../media/utils";
import { ConfigurationResponse } from "@/types";

const { Text } = Typography;

export const FlagPage: React.FC = () => {
  const t = useTranslate();

  const {
    data: configurationData,
    isLoading,
    isRefetching,
  } = useList<ConfigurationResponse>({
    resource: `api/configuration`,
  });

  const tableData = useMemo(
    () =>
      (
        configurationData?.data.filter((item) => item.type === "bool") ?? []
      ).sort((a, b) => {
        if (a.value !== b.value) {
          return a.value === "true" ? -1 : 1;
        }
        return a.id.localeCompare(b.id);
      }),
    [configurationData]
  );

  const columns: TableColumnsType<ConfigurationResponse> = [
    {
      title: t("featureFlags.index.id"),
      dataIndex: "id",
      key: "id",
      width: 350,
      sorter: (a, b) => a.id.localeCompare(b.id),
    },
    {
      title: t("featureFlags.index.value"),
      dataIndex: "value",
      key: "value",
      width: 140,
      align: "center",
      filters: [
        { text: "Enabled", value: "true" },
        { text: "Disabled", value: "false" },
      ],
      onFilter: (value, record) => record.value === value,
      render: (value: string) => {
        const isEnabled = value === "true";
        return (
          <Tag
            icon={isEnabled ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
            color={isEnabled ? "success" : "default"}
          >
            {isEnabled ? "Enabled" : "Disabled"}
          </Tag>
        );
      },
    },
    {
      title: t("featureFlags.index.type"),
      dataIndex: "type",
      key: "type",
      width: 100,
      align: "center",
      render: (type: string) => (
        <Tag color="blue" bordered={false}>
          {type}
        </Tag>
      ),
    },
    {
      title: t("featureFlags.index.creationDate"),
      dataIndex: "created",
      key: "created",
      width: 200,
      sorter: (a, b) =>
        new Date(a.created).getTime() - new Date(b.created).getTime(),
      render: (text) => (
        <Text type="secondary">
          <DateField value={addDateTz(text)} format="LLL" />
        </Text>
      ),
    },
    {
      title: t("featureFlags.index.actions"),
      key: "actions",
      width: 180,
      align: "center",
      render: (_, record: ConfigurationResponse) => (
        <Space size="small">
          <EditButton
            size="small"
            type="default"
            accessControl={{ enabled: false }}
            recordItemId={record.id}
          >
            {t("featureFlags.index.edit")}
          </EditButton>
          <DeleteButton
            size="small"
            type="default"
            danger
            resource="api/configuration"
            accessControl={{ enabled: false }}
            recordItemId={record.id}
          >
            {t("featureFlags.index.delete")}
          </DeleteButton>
        </Space>
      ),
    },
  ];

  return (
    <Authenticated key="feature_flags_page">
      <CanAccess
        resource="feature_flags"
        action="read"
        fallback={
          <Result
            status={403}
            title={t("pages.system.detail.accessForbidden")}
          />
        }
      >
        <List resource="feature_flags">
          <Card title={t("featureFlags.index.title")}>
            <Table
              rowKey="id"
              loading={isLoading || isRefetching}
              dataSource={tableData}
              columns={columns}
              pagination={false}
            />
          </Card>
        </List>
      </CanAccess>
    </Authenticated>
  );
};
